import { createElement } from '../lib/createElement'
import { clamp } from '../lib/math'

export type ContentItem = {
	src: string
	title?: string
}

export default new class Lightbox {
	#parentElement = createElement('ui-lightbox')
	#contentElement = createElement('div', { className: 'content' })

	// Buttons.
	#closeButton = createElement('button', { className: 'close' })
	#nextButton = createElement('button', { className: 'stepper next' })
	#prevButton = createElement('button', { className: 'stepper prev' })

	#currentContent: ContentItem[] | null
	#currentIndex: number | null

	public get currentIndex(): number {
		return this.#currentIndex
	}

	constructor() {
		this.#parentElement.appendChild(this.#contentElement)
		this.#parentElement.appendChild(this.#closeButton)
		this.#parentElement.appendChild(this.#nextButton)
		this.#parentElement.appendChild(this.#prevButton)

		this.#closeButton.addEventListener('click', () => this.close())
		this.#nextButton.addEventListener('click', () => this.setIndex(this.#currentIndex + 1))
		this.#prevButton.addEventListener('click', () => this.setIndex(this.#currentIndex - 1))

		addEventListener('DOMContentLoaded', () => {
			// Initialize everything wth a `data-lb-interactive` attribute.
			// If it belongs to a group, collect all group members. Then open the lightbox with the collected data.
			const interactiveImages = Array.from(document.querySelectorAll('[data-lb-interactive]')) as HTMLElement[]
			for (const image of interactiveImages) {
				image.addEventListener('click', (event) => {
					let items : ContentItem[]
					const group = image.dataset.lbGroup

					if (group) {
						items = Array.from(document.querySelectorAll(`[data-lb-group="${group}"]`))
							.map((element: HTMLElement) => {
								return {
									src: element.dataset.lbImage,
									title: element.dataset.lbTitle,
								}
							})
					}
					else {
						items = [{
							src: image.dataset.lbImage,
							title: image.dataset.lbTitle,
						}]
					}

					const index = items.findIndex(item => item.src == image.dataset.lbImage)
					this.open(items, index ?? 0)
				})
			}
		})
	}

	public setIndex(index: number) {
		const nextIndex = clamp(index, 0, this.#currentContent.length - 1)
		if (nextIndex === this.#currentIndex) {
			return
		}

		this.#currentIndex = nextIndex

		// Set the content.
		this.#contentElement.innerHTML = ''
		this.#contentElement.classList.remove('loaded')

		const item = this.#currentContent[this.#currentIndex]

		// Images.
		if (/\.(png|jpg|jpeg|gif|bmp)$/gi.test(item.src)) {
			const image = new Image()
			image.onload = () => {
				this.#contentElement.classList.add('loaded')
				this.#contentElement.appendChild(image)
			}
			image.src = item.src
		}
		// Video file.
		else if (/\.(mp4|webm|ogg)$/gi.test(item.src)) {
			const video = createElement(
				'video',
				{ controls: true },
				createElement('source', { src: item.src })
			)
			this.#contentElement.appendChild(video)
			this.#contentElement.classList.add('loaded')
		}
		// Youtube / Vimeo iframe.
		else if (/(vimeo|youtube|youtu\.be)/gi.test(item.src)) {
			const iframe = createElement('iframe', {
				allowFullscreen: true,
				src: item.src,
			})
			this.#contentElement.appendChild(iframe)
			this.#contentElement.classList.add('loaded')
		}

		this.#nextButton.classList.toggle('disabled', this.#currentIndex == this.#currentContent.length - 1)
		this.#prevButton.classList.toggle('disabled', this.#currentIndex == 0)
	}

	public open(content: string): void
	public open(content: (ContentItem | string)[], index?: number): void
	public open(content: string | (ContentItem | string)[], index: number = 0) {
		if (typeof content == 'string') {
			this.#currentContent = [{ src: content }]
		}
		else {
			this.#currentContent = content.map(item => {
				if (typeof item == 'string') {
					return { src: item }
				}
				return item
			})
		}

		this.setIndex(index ?? 0)
		document.body.appendChild(this.#parentElement)
		document.addEventListener('keydown', this.onKeyDown)
	}

	public close() {
		this.#contentElement.innerHTML = ''
		this.#currentContent = null
		this.#currentIndex = null
		this.#parentElement.remove()
		document.removeEventListener('keydown', this.onKeyDown)
	}

	private onKeyDown = (event: KeyboardEvent) => {
		switch (event.key) {
			case 'ArrowLeft':
				this.setIndex(this.#currentIndex - 1)
				break
			case 'ArrowRight':
				this.setIndex(this.#currentIndex + 1)
				break
			case 'Escape':
				this.close()
				break
		}
	}
}